/**
 * activityDetailsAdapter — l'activité telle que l'API la sert → `UIActivityDetails`.
 *
 * Portage littéral des dérivations de `student/activity/todo/TodoModal.js`, chemin PARENT :
 *  - l'en-tête vient de `components/ActivityItemInfo.js` (titre du support à défaut celui de
 *    l'activité, chapitre à défaut la matière, chrono « passé / prévu ») ;
 *  - l'énoncé vient de `execute/MaterialDisplayModal.js` (`url` > `attachments` > `text`) ;
 *  - le rendu vient des journaux d'activité (`step === 2`, `action === "step_media_sent"`), la
 *    correction en PREMIÈRE image, comme `TodoModal.js` l'assemble ;
 *  - les points de la correction viennent de `correction_points` — le legacy ne les montrait qu'au
 *    tuteur (`isTutorViewer`), mais sans eux les pastilles de l'image ne disent rien au parent.
 *
 * Ce qui n'est pas ici est ce que le parent n'atteint pas : le tableau blanc (`isLiveMode` + rôle
 * tuteur) et l'onglet Quizz, non porté.
 */
import { type UISubject } from "../../../ui/types"
import {
    type UIActivityCorrectionAnchor,
    type UIActivityCorrectionPoint,
    type UIActivityCorrectionPointType,
    type UIActivityDetails,
    type UIActivityDetailsAction,
    type UIActivityDisplayMode,
    type UIActivityMedia,
    type UIActivityStatement,
    type UIActivityStatus,
    type UIActivityWork,
} from "../../../ui/types"
import { toUISubject, type ApiSchoolSubject } from "../shared/schoolSubjectAdapter"

/** Formes lâches : tout est optionnel, rien n'est importé d'un modèle (`ports/CLAUDE.md` § 5). */
export type ApiActivityLog = {
    step?: number
    action?: string
    work_attachment?: string | null
    step_data?: { chrono_duration?: number | null } | null
}

export type ApiActivityMaterial = {
    id?: string | number
    title?: string | null
    text?: string | null
    url?: string | null
    attachments?: (string | { uri?: string })[] | null
    media_type?: string | null
    estimated_duration?: number | null
}

export type ApiActivityDetails = {
    id?: string | number
    title?: string | null
    category?: string | null
    status?: string | null
    started_at?: string | null
    locked_at?: string | null
    finished_at?: string | null
    duration?: number | null
    estimated_duration?: number | null
    media_count?: number | null
    school_subject_id?: string | number | null
    activity_template_id?: string | number | null
    /** Le chapitre libre porté par l'activité elle-même — « Redonner » le renvoie tel quel. */
    chapter?: unknown
    student_chapter?: {
        chapter?: { id?: string | number | null; name?: string | null; class?: string | null } | null
    } | null
    material?: ApiActivityMaterial | null
    activity_logs?: ApiActivityLog[] | null
    correction_image?: string | null
    correction_points?: ApiCorrectionPoint[] | null
}

/** Un point de `ActivityResource::buildCorrectionPoints` — servi seulement sur une correction terminée. */
export type ApiCorrectionPoint = {
    index?: number | null
    type?: string | null
    notion?: string | null
    skill_part_name?: string | null
    inconnue?: boolean | null
    explication?: string | null
    anchor?: { x?: number | null; y?: number | null; w?: number | null; h?: number | null; page?: number | null } | null
}

const CORRECTION_POINT_TYPES: Record<string, UIActivityCorrectionPointType> = {
    erreur: "error",
    vigilance: "warning",
    réussite: "success",
}

/** Le mot que la fiche écrit quand la notion n'a pas pu être rattachée — jamais une notion inventée. */
export const UNKNOWN_NOTION_LABEL = "Notion à préciser"

/**
 * L'ancre d'un point, quand ses quatre coordonnées sont des nombres (`hasUsableAnchor` du legacy).
 * La page est ramenée dans la copie — un modèle qui compte une page de trop ne perd pas sa pastille
 * (`anchorPageIndex`, comme le rendu serveur).
 */
function toUIAnchor(anchor: ApiCorrectionPoint["anchor"], pagesCount: number): UIActivityCorrectionAnchor | undefined {
    const { x, y, w, h } = anchor ?? {}
    if (![x, y, w, h].every((v) => typeof v === "number" && !Number.isNaN(v))) return undefined
    const page = Math.min(Math.max(0, anchor?.page ?? 0), Math.max(0, pagesCount - 1))
    return { x: x!, y: y!, w: w!, h: h!, page }
}

/**
 * Les points de la correction, dans l'ordre des pastilles. La notion suit la carte tuteur
 * (`CorrectionPointCard.js`) : la compétence rattachée, à défaut la notion de l'IA, et « Notion à
 * préciser » quand rien n'a pu être rattaché.
 */
export function toUICorrectionPoints(
    points: ApiCorrectionPoint[] | null | undefined,
    pagesCount = 1
): UIActivityCorrectionPoint[] {
    return (points ?? [])
        .filter((point): point is ApiCorrectionPoint & { index: number } => typeof point?.index === "number")
        .sort((a, b) => a.index - b.index)
        .map((point) => ({
            index: point.index,
            type: CORRECTION_POINT_TYPES[point.type ?? ""] ?? "other",
            notion: point.inconnue
                ? UNKNOWN_NOTION_LABEL
                : point.skill_part_name || point.notion || UNKNOWN_NOTION_LABEL,
            explanation: point.explication || undefined,
            anchor: toUIAnchor(point.anchor, pagesCount),
        }))
}

/** `common/components/pdf/pdfHelpers.js` — recopié, `common/**` étant interdit aux ports. */
function isPdfUri(uri: string | undefined | null): boolean {
    return !!uri && (uri.startsWith("data:application/pdf") || uri.endsWith(".pdf"))
}

/** `MaterialDisplayModal.getImageBody` accepte une chaîne ou un objet `{ uri }`. */
function attachmentUri(attachment: string | { uri?: string } | null | undefined): string | null {
    if (typeof attachment === "string") return attachment
    return attachment?.uri ?? null
}

/**
 * Le statut affiché — `status ?? statusActivity`, masqué sur « à faire »
 * (`ActivityItemInfo.showStatusBadge`). Les deux états de chrono viennent de `TodoModal.js` :
 * une activité terminée n'en porte aucun, sinon `locked_at` distingue « en cours » de « en pause ».
 * Les comparaisons restent `!== null` (et non `!= null`) pour rester le legacy au caractère près.
 */
export function activityDisplayStatus(raw: ApiActivityDetails): UIActivityStatus | undefined {
    const chrono =
        raw.finished_at != null
            ? null
            : raw.started_at !== null && raw.locked_at !== null
              ? "ongoing"
              : raw.started_at !== null && raw.locked_at === null
                ? "started"
                : null
    const status = chrono ?? raw.status ?? null
    if (status == null || status === "todo") return undefined
    return status as UIActivityStatus
}

/** L'énoncé : `url` d'abord, puis les pièces jointes, le texte accompagnant les deux. */
export function toUIActivityStatement(material: ApiActivityMaterial | null | undefined): UIActivityStatement {
    const text = material?.text ?? undefined
    const media: UIActivityMedia[] = []

    if (material?.url) {
        media.push({ kind: "link", url: material.url, title: material.title ?? undefined })
    } else {
        const attachments = (material?.attachments ?? [])
            .map(attachmentUri)
            .filter((uri): uri is string => uri != null && uri !== "")
        const first = attachments[0]
        if (material?.media_type === "pdf" || isPdfUri(first)) {
            if (first != null) media.push({ kind: "pdf", uri: first, name: material?.title ?? undefined })
        } else {
            for (const uri of attachments) media.push({ kind: "image", uri })
        }
    }

    return { text: text ?? undefined, media }
}

/**
 * Ce que l'élève a rendu. `media_count` est la garde du legacy (`hasRendu`) : sans rendu, ni
 * l'onglet ni le bloc n'existent. Une activité écrite rend des photos, les autres un
 * enregistrement — c'est `category === "write"` qui tranche (`TodoModal.js:1854`).
 */
export function toUIActivityWork(
    raw: ApiActivityDetails,
    cleanUrl: (url: string | null | undefined) => string | null
): UIActivityWork | null {
    if ((raw.media_count ?? 0) <= 0) return null

    const logs = raw.activity_logs ?? []
    const mediaLogs = logs.filter((log) => log?.step === 2 && log?.action === "step_media_sent")

    if (raw.category === "write") {
        const workImages = mediaLogs
            .map((log) => log?.work_attachment)
            .filter((uri): uri is string => uri != null && uri !== "")
        const correction = raw.correction_image
        const hasCorrection = correction != null && correction !== ""
        const correctionPoints = hasCorrection ? toUICorrectionPoints(raw.correction_points, workImages.length) : []
        // Comme `anchoredCorrection` de la TodoModal : les pastilles cliquables seulement quand
        // TOUS les points sont ancrés — sinon l'image cuite, qui les porte toutes.
        const anchored =
            correctionPoints.length > 0 && workImages.length > 0 && correctionPoints.every((p) => p.anchor != null)
        return {
            images: correction ? [correction, ...workImages] : workImages,
            hasCorrection,
            correctionPoints: correctionPoints.length > 0 ? correctionPoints : undefined,
            copyPages: anchored ? workImages : undefined,
        }
    }

    // Le legacy lit la durée sur le journal `step_finished`, pas sur celui du média.
    const chronoLog = logs.find((log) => log?.step === 2 && log?.action === "step_finished")
    const audioUri = cleanUrl(mediaLogs[0]?.work_attachment)
    return {
        images: [],
        audioUri: audioUri ?? undefined,
        audioDurationSeconds: chronoLog?.step_data?.chrono_duration ?? undefined,
    }
}

export type ToUIActivityDetailsOptions = {
    /** Le catalogue des matières (`staticData.school_subjects`), indexé plus haut par l'appelant. */
    subject?: ApiSchoolSubject | null
    /** `cleanMediaUrl` — réécrit le domaine de dev. Le legacy ne l'applique QU'À l'audio. */
    cleanUrl: (url: string | null | undefined) => string | null
}

export function toUIActivityDetails(
    raw: ApiActivityDetails | null | undefined,
    options: ToUIActivityDetailsOptions
): UIActivityDetails | null {
    if (raw?.id == null) return null

    const subject: UISubject = toUISubject(options.subject)
    const chapterName = raw.student_chapter?.chapter?.name ?? undefined

    return {
        id: raw.id,
        title: raw.material?.title ?? raw.title ?? "Activité",
        subject,
        chapterName: chapterName !== "" ? chapterName : undefined,
        status: activityDisplayStatus(raw),
        // `ActivityItemInfo` passe `count={0}` à la pastille : la fiche n'a jamais dit « Fait 2x ».
        estimatedDurationMinutes: raw.estimated_duration ?? undefined,
        // `duration` est en secondes, et le legacy écrit « - » quand elle vaut zéro.
        spentDurationSeconds: raw.duration ? raw.duration : undefined,
        statement: toUIActivityStatement(raw.material),
        work: toUIActivityWork(raw, options.cleanUrl),
    }
}

/**
 * Les vues offertes. Le legacy en connaît cinq ; restent celles que le parent atteint —
 * « Tableau blanc » demande un partage d'écran de tuteur en séance et « Quizz » est une surface
 * de relecture tuteur, non portée. L'écran double n'apparaît qu'au-delà de 800 px, comme
 * `canShowSplit`.
 */
export function activityDisplayModes(
    raw: ApiActivityDetails | null | undefined,
    wide: boolean
): UIActivityDisplayMode[] {
    const hasWork = (raw?.media_count ?? 0) > 0
    const modes: UIActivityDisplayMode[] = ["material"]
    if (hasWork) modes.push("work")
    if (hasWork && wide) modes.push("split")
    return modes
}

/** La vue d'ouverture : le rendu dès qu'il existe, l'énoncé sinon (`TodoModal.getDefaultMode`). */
export function defaultActivityDisplayMode(
    raw: ApiActivityDetails | null | undefined,
    wide: boolean
): UIActivityDisplayMode {
    if ((raw?.media_count ?? 0) > 0) return wide ? "split" : "work"
    return "material"
}

/**
 * Les gestes offerts, deux au plus. Depuis les archives, il n'y en a qu'un ; sinon « Archiver »,
 * précédé de « Redonner » quand l'activité est terminée (`renderHeaderActions`, `renderMenuItems`).
 */
export function activityDetailsActions(
    raw: ApiActivityDetails | null | undefined,
    fromArchived: boolean
): UIActivityDetailsAction[] {
    if (fromArchived) return ["restore"]
    return raw?.finished_at != null ? ["redo", "archive"] : ["archive"]
}

/**
 * Le corps de « Redonner » — `TodoModal.onPress`, à la clé près. En mode parent le jeton est
 * celui de l'enfant, donc c'est `activityTodoAsStudent` qui part, sans identifiant dans l'URL.
 */
export function redoActivityBody(
    raw: ApiActivityDetails | null | undefined,
    extra: { activityTemplateId?: string | number | null; schoolSubjectId?: string | number | null }
): Record<string, unknown> {
    const chapter = raw?.student_chapter?.chapter
    return {
        activity_template_id: extra.activityTemplateId ?? undefined,
        school_subject_id: extra.schoolSubjectId ?? undefined,
        material_id: raw?.material?.id,
        material_chapter: raw?.chapter,
        estimated_duration: raw?.estimated_duration,
        chapter_id: chapter?.id,
        chapter_name: chapter?.name,
        chapter_class: chapter?.class,
    }
}
